import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  isWiesnAccount,
  isDhlStickEnabled,
  setDhlStickEnabled,
  getDhlStickIp,
  setDhlStickIp,
  isBevollmaechtigterEnabled,
  setBevollmaechtigterEnabled,
  getDhlStickStatus,
  wakeupDhlStick,
  getDhlStickQueueMode,
  setDhlStickQueueMode,
  isPhotoSchnelleinlagerungEnabled,
  setPhotoSchnelleinlagerungEnabled,
  getLocalStickTasks,
  executeLocalStickTask,
  removeLocalStickTask,
  clearLocalStickTasks,
  finishDhlStickSchnelleinlagerung,
  safeHomeDhlStick,
  removeLastDhlStickItem,
  enableVirtualKeyboardDhlStick,
  otaUpdateDhlStick,
  tapDhlStickCoord,
  stickFetch,
  analyzeDhlScreenCanvas,
  requestDhlScreenSnapshot,
  fetchLatestDhlScreenSnapshot,
  isScannerEyeBridgeActive,
  setScannerEyeBridgeActive,
} from '../services/dhlUsbStick';
import { connectSSE } from '../services/apiService';
import { relaySignal } from '../services/stationService';
import { analyzeImage } from '../services/mlService';

const STAMPS_STORAGE_KEY = 'kiosk_stamps_state';
const EYE_CROP_KEY = 'kiosk_dhl_scanner_eye_crop';

const DEFAULT_EYE_CROP = { x: 0.18, y: 0.08, w: 0.64, h: 0.84 };

function loadEyeCrop() {
  try {
    const raw = JSON.parse(localStorage.getItem(EYE_CROP_KEY) || 'null');
    if (raw && typeof raw.x === 'number' && typeof raw.w === 'number') return raw;
  } catch { /* ignore */ }
  return DEFAULT_EYE_CROP;
}

const DEFAULT_STAMPS = [
  { id: 'std-85', label: '0,85 € Standardbrief', count: 0, totalBooked: 0 },
  { id: 'komp-100', label: '1,00 € Kompaktbrief', count: 0, totalBooked: 0 },
  { id: 'gross-160', label: '1,60 € Großbrief', count: 0, totalBooked: 0 },
  { id: 'maxi-275', label: '2,75 € Maxibrief', count: 0, totalBooked: 0 },
  { id: 'post-95', label: '0,95 € Postkarte', count: 0, totalBooked: 0 },
];

function loadStampsState() {
  try {
    const raw = localStorage.getItem(STAMPS_STORAGE_KEY);
    if (!raw) return { items: DEFAULT_STAMPS, history: [] };
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed?.items) && parsed.items.length > 0 ? parsed.items : DEFAULT_STAMPS;
    const history = Array.isArray(parsed?.history) ? parsed.history : [];
    return { items, history };
  } catch {
    return { items: DEFAULT_STAMPS, history: [] };
  }
}

function saveStampsState(next) {
  try {
    localStorage.setItem(STAMPS_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('kiosk:stamps-updated', { detail: next }));
  } catch { /* ignore */ }
}

/**
 * 🥨 Eigenständiges Wiesn-Shop-Modul (STRIKT nur für `wiesn2022@gmail.com`)
 *
 * Enthält:
 *  1. 🔌 Stick-Steuerung, Notfall-Buttons, Live-Scanner-Bild ("Scanner-Auge") & Warteschlange
 *  2. 👁️ Kamera-Brücke ("Scanner-Auge"): Macht das Kamera-Station-Handy vor Ort zum
 *     Echtzeit-Screenreader & lokalen WLAN-Relais für Scanner 1
 *  3. 📮 Briefmarken-Verkauf (10er-Strichliste mit Alarm ab 10 verkauften Marken zum Ausbuchen)
 */
export default function WiesnShopModule({ colors = {}, onOpenDhlCockpit }) {
  const allowed = isWiesnAccount();
  const [activeTab, setActiveTab] = useState(null); // null (zugeklappt) | 'queue' | 'stamps'
  const [stickEnabled, setStickEnabledState] = useState(() => isDhlStickEnabled());
  const [stickIpInput, setStickIpInput] = useState(() => getDhlStickIp());
  const [bevollmEnabled, setBevollmEnabledState] = useState(() => isBevollmaechtigterEnabled());
  const [queueMode, setQueueModeState] = useState(() => getDhlStickQueueMode());
  const [photoSchnell, setPhotoSchnellState] = useState(() => isPhotoSchnelleinlagerungEnabled());
  const [tasks, setTasks] = useState(() => getLocalStickTasks());
  const [stickStatus, setStickStatus] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [actionToast, setActionToast] = useState('');
  const [stampsState, setStampsState] = useState(() => loadStampsState());
  const [newStampLabel, setNewStampLabel] = useState('');

  // 👁️ Scanner-Auge (Kamera-Station über Scanner 1 + Live-Kontrollbild auf Arbeits-Handy)
  const [scannerEyeActive, setScannerEyeActive] = useState(() => isScannerEyeBridgeActive());
  const [scannerEyeAutoLoop, setScannerEyeAutoLoop] = useState(true);
  const [eyeCrop, setEyeCrop] = useState(() => loadEyeCrop());
  const [showCropSliders, setShowCropSliders] = useState(false);
  const [scannerSnap, setScannerSnap] = useState(null);
  const [eyeCamError, setEyeCamError] = useState('');
  const [ocrRunning, setOcrRunning] = useState(false);

  const eyeVideoRef = useRef(null);
  const eyeStreamRef = useRef(null);
  const eyeCropRef = useRef(eyeCrop);
  eyeCropRef.current = eyeCrop;
  const scannerEyeActiveRef = useRef(scannerEyeActive);
  scannerEyeActiveRef.current = scannerEyeActive;
  const stickIpRef = useRef(stickIpInput);
  stickIpRef.current = stickIpInput;

  const dark = Boolean(colors?.darkMode);

  const showNotice = useCallback((msg) => {
    setActionToast(msg);
    setTimeout(() => {
      setActionToast((prev) => (prev === msg ? '' : prev));
    }, 4200);
  }, []);

  const refreshStick = useCallback(async (ipOverride) => {
    if (!isWiesnAccount()) return;
    const st = await getDhlStickStatus(ipOverride || stickIpRef.current);
    setStickStatus(st);
  }, []);

  // 📸 Schneidet das Scanner-1-Display aus dem Kamerabild des Kamera-Station-Handys aus,
  // führt die 15ms-Farb-/Zustandsanalyse aus und sendet das Bild per SSE an Arbeits-Handy + Server
  const captureAndBroadcastScannerSnap = useCallback(async (reason = '', runOcr = false) => {
    const video = eyeVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return null;
    try {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const r = eyeCropRef.current || DEFAULT_EYE_CROP;
      const sx = Math.round(r.x * vw);
      const sy = Math.round(r.y * vh);
      const sw = Math.max(40, Math.round(r.w * vw));
      const sh = Math.max(60, Math.round(r.h * vh));

      const canvas = document.createElement('canvas');
      const targetW = 320;
      const targetH = Math.max(180, Math.round((sh / sw) * targetW));
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH);

      const analysis = analyzeDhlScreenCanvas(canvas);
      const preview = canvas.toDataURL('image/jpeg', 0.68);

      let ocrText = '';
      if (runOcr) {
        setOcrRunning(true);
        try {
          const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.82));
          if (blob) {
            const mlRes = await analyzeImage(blob);
            ocrText = String(mlRes?.ocrResult || '').trim().slice(0, 280);
          }
        } catch { /* ignore */ }
        setOcrRunning(false);
      }

      const payload = {
        preview,
        state: analysis.state,
        label: analysis.label,
        color: analysis.color,
        hasListItem: Boolean(analysis.hasListItem),
        popupSuspected: Boolean(analysis.popupSuspected),
        isAsleep: Boolean(analysis.isAsleep),
        reason: reason || '',
        ocrText: ocrText || undefined,
        ts: Date.now(),
      };
      setScannerSnap(payload);
      window.dispatchEvent(new CustomEvent('kiosk:dhl-screen-snap', { detail: payload }));
      await relaySignal('station:dhl-screen-snap', payload).catch(() => {});
      return payload;
    } catch {
      return null;
    }
  }, []);

  // Startet / stoppt die Rückkamera auf dem Kamera-Station-Handy ("Scanner-Auge")
  useEffect(() => {
    if (!allowed || !scannerEyeActive) {
      if (eyeStreamRef.current) {
        eyeStreamRef.current.getTracks().forEach((t) => t.stop());
        eyeStreamRef.current = null;
      }
      return undefined;
    }
    let cancelled = false;
    let wakeLockObj = null;

    const startEyeCamera = async () => {
      setEyeCamError('');
      try {
        if (navigator.wakeLock?.request) {
          wakeLockObj = await navigator.wakeLock.request('screen').catch(() => null);
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        eyeStreamRef.current = stream;
        if (eyeVideoRef.current) {
          eyeVideoRef.current.srcObject = stream;
          await eyeVideoRef.current.play().catch(() => {});
        }
        setTimeout(() => {
          if (!cancelled) captureAndBroadcastScannerSnap('Kamera-Auge gestartet');
        }, 900);
      } catch (err) {
        if (!cancelled) {
          setEyeCamError(err?.message || 'Kamera konnte nicht gestartet werden');
        }
      }
    };

    startEyeCamera();
    return () => {
      cancelled = true;
      if (wakeLockObj && typeof wakeLockObj.release === 'function') {
        wakeLockObj.release().catch(() => {});
      }
      if (eyeStreamRef.current) {
        eyeStreamRef.current.getTracks().forEach((t) => t.stop());
        eyeStreamRef.current = null;
      }
    };
  }, [allowed, scannerEyeActive, captureAndBroadcastScannerSnap]);

  // Periodischer Auto-Snapshot im Scanner-Auge-Modus (alle 4,5 Sek.), damit Arbeits-Handy & Server live mitsehen
  useEffect(() => {
    if (!allowed || !scannerEyeActive || !scannerEyeAutoLoop) return undefined;
    const iv = setInterval(() => {
      captureAndBroadcastScannerSnap('Live-Monitor');
    }, 4500);
    return () => clearInterval(iv);
  }, [allowed, scannerEyeActive, scannerEyeAutoLoop, captureAndBroadcastScannerSnap]);

  // SSE-Verbindung für Scanner-Auge-Snapshots & WLAN-Brücken-Relay
  useEffect(() => {
    if (!allowed) return undefined;
    fetchLatestDhlScreenSnapshot().then((snap) => {
      if (snap && snap.preview) {
        setScannerSnap(snap);
        window.dispatchEvent(new CustomEvent('kiosk:dhl-screen-snap', { detail: snap }));
      }
    }).catch(() => {});

    const close = connectSSE({
      'station:dhl-screen-snap': (data) => {
        if (!data || !data.preview) return;
        setScannerSnap(data);
        window.dispatchEvent(new CustomEvent('kiosk:dhl-screen-snap', { detail: data }));
      },
      'station:dhl-stick-res': (data) => {
        if (!data || !data.reqId) return;
        window.dispatchEvent(new CustomEvent('kiosk:dhl-stick-relay-res', { detail: data }));
      },
      'station:dhl-stick-req': async (data) => {
        if (!scannerEyeActiveRef.current || !data || !data.reqId || !data.path) return;
        const result = await stickFetch(data.path, {
          method: data.method || 'GET',
          body: data.body,
          ip: data.ip || stickIpRef.current,
          noBridgeFallback: true,
        });
        await relaySignal('station:dhl-stick-res', { reqId: data.reqId, result }).catch(() => {});
        setTimeout(() => captureAndBroadcastScannerSnap(`Brücke: ${data.path}`), 1100);
      },
      'station:dhl-snap-req': (data) => {
        if (!scannerEyeActiveRef.current) return;
        const waitMs = Math.min(8000, Math.max(200, Number(data?.delayMs) || 900));
        setTimeout(() => {
          captureAndBroadcastScannerSnap(data?.reason || 'Angefordert');
        }, waitMs);
      },
    });
    return () => {
      try { close?.(); } catch { /* ignore */ }
    };
  }, [allowed, captureAndBroadcastScannerSnap]);

  useEffect(() => {
    if (!allowed) return undefined;
    const onQueueChange = () => {
      setTasks(getLocalStickTasks());
      setQueueModeState(getDhlStickQueueMode());
      setPhotoSchnellState(isPhotoSchnelleinlagerungEnabled());
      setStickEnabledState(isDhlStickEnabled());
      setBevollmEnabledState(isBevollmaechtigterEnabled());
      setStickIpInput(getDhlStickIp());
    };
    const onStampsChange = () => {
      setStampsState(loadStampsState());
    };
    window.addEventListener('kiosk:dhl-stick-queue', onQueueChange);
    window.addEventListener('kiosk:custom-features', onQueueChange);
    window.addEventListener('kiosk:stamps-updated', onStampsChange);
    refreshStick();
    const iv = setInterval(refreshStick, 6000);
    return () => {
      window.removeEventListener('kiosk:dhl-stick-queue', onQueueChange);
      window.removeEventListener('kiosk:custom-features', onQueueChange);
      window.removeEventListener('kiosk:stamps-updated', onStampsChange);
      clearInterval(iv);
    };
  }, [allowed, refreshStick]);

  // Strikte Konto-Isolierung: Andere Konten sehen dieses Modul niemals!
  if (!allowed) return null;

  const pendingReviewCount = tasks.filter((t) => t.status === 'pending_review').length;
  const errorCount = tasks.filter((t) => t.status === 'error').length;
  const openSchnellCount = Number(stickStatus?.uncommittedSchnellCount) || 0;
  const stampsReadyToBook = stampsState.items.filter((it) => (Number(it.count) || 0) >= 10);
  const totalStampsReadyBlocks = stampsState.items.reduce(
    (sum, it) => sum + Math.floor((Number(it.count) || 0) / 10),
    0
  );

  const handleToggleQueueMode = (nextMode) => {
    setDhlStickQueueMode(nextMode);
    setQueueModeState(nextMode);
  };

  const handleTogglePhotoSchnell = () => {
    const next = !photoSchnell;
    setPhotoSchnelleinlagerungEnabled(next);
    setPhotoSchnellState(next);
  };

  const handleToggleScannerEye = () => {
    const next = !scannerEyeActive;
    setScannerEyeBridgeActive(next);
    setScannerEyeActive(next);
    if (next) {
      setActiveTab('queue');
      showNotice('👁️ Scanner-Auge & WLAN-Brücke aktiviert! Richte die Kamera auf Scanner 1.');
    }
  };

  const updateCropField = (key, val) => {
    const next = { ...eyeCrop, [key]: Number(val) };
    setEyeCrop(next);
    try { localStorage.setItem(EYE_CROP_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const handleExecuteTask = async (taskId, forceNewId = false) => {
    setBusyId(taskId);
    try {
      await executeLocalStickTask(taskId, { forceNewId });
      setTasks(getLocalStickTasks());
      setTimeout(refreshStick, 700);
    } finally {
      setBusyId('');
    }
  };

  const handleFinishSchnell = async () => {
    setBusyId('finish-schnell');
    try {
      await finishDhlStickSchnelleinlagerung(stickIpInput);
      showNotice('✅ Schnelleinlagerung am DHL-Scanner abgeschlossen!');
      await refreshStick();
    } finally {
      setBusyId('');
    }
  };

  const handleSafeHome = async () => {
    setBusyId('safe-home');
    try {
      const res = await safeHomeDhlStick(stickIpInput);
      showNotice(res?.ok ? '🏠 DHL-Scanner zurück auf Startbildschirm gesetzt!' : `⚠️ ${res?.error || 'Stick nicht erreichbar'}`);
      await refreshStick();
    } finally {
      setBusyId('');
    }
  };

  const handleRemoveLastItem = async () => {
    setBusyId('remove-last');
    try {
      const res = await removeLastDhlStickItem(stickIpInput);
      showNotice(res?.ok ? '🗑️ Oberste Sendungsnummer aus DHL-Liste entfernt!' : `⚠️ ${res?.error || 'Stick nicht erreichbar'}`);
      await refreshStick();
    } finally {
      setBusyId('');
    }
  };

  const handleEnableVkbd = async () => {
    setBusyId('enable-vkbd');
    try {
      const res = await enableVirtualKeyboardDhlStick(stickIpInput);
      showNotice(res?.ok ? '⌨️ Bildschirmtastatur auf DHL-Scanner aktiviert!' : `⚠️ ${res?.error || 'Stick nicht erreichbar'}`);
    } finally {
      setBusyId('');
    }
  };

  const handleOtaUpdate = async () => {
    if (!window.confirm(`Stick-Firmware (${stickIpInput}) jetzt drahtlos per WLAN auf Version 2 aktualisieren? Dauert ca. 8 Sekunden.`)) return;
    setBusyId('ota');
    showNotice('⏳ Übertrage neue Firmware an DHL-Stick…');
    try {
      const res = await otaUpdateDhlStick(stickIpInput);
      if (res?.ok) {
        showNotice('✅ Firmware aktualisiert! Stick startet neu (ca. 5 Sek.)…');
        setTimeout(() => refreshStick(stickIpInput), 5500);
      } else {
        showNotice(`❌ OTA fehlgeschlagen: ${res?.error || 'Unbekannter Fehler'}`);
      }
    } finally {
      setBusyId('');
    }
  };

  // Klick direkt auf das Live-Kontrollbild des DHL-Scanners -> sendet echten Maus-Klick an diese (X%, Y%)-Position!
  const handleLiveSnapTap = async (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const xPct = Math.max(0, Math.min(100, +(((e.clientX - rect.left) / rect.width) * 100).toFixed(1)));
    const yPct = Math.max(0, Math.min(100, +(((e.clientY - rect.top) / rect.height) * 100).toFixed(1)));
    showNotice(`👆 Klick an Scanner gesendet (${xPct}% / ${yPct}%)…`);
    await tapDhlStickCoord({ xPct, yPct, click: true, ip: stickIpInput });
    if (scannerEyeActive) {
      setTimeout(() => captureAndBroadcastScannerSnap(`Tap ${xPct}/${yPct}`), 850);
    }
  };

  const updateStampCount = (id, delta) => {
    const next = {
      ...stampsState,
      items: stampsState.items.map((item) => {
        if (item.id !== id) return item;
        const nextCount = Math.max(0, (Number(item.count) || 0) + delta);
        return { ...item, count: nextCount };
      }),
    };
    setStampsState(next);
    saveStampsState(next);
  };

  const bookOutTenStamps = (id) => {
    const target = stampsState.items.find((it) => it.id === id);
    if (!target || (Number(target.count) || 0) < 10) return;
    const nowStr = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const next = {
      items: stampsState.items.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          count: Math.max(0, (Number(item.count) || 0) - 10),
          totalBooked: (Number(item.totalBooked) || 0) + 10,
        };
      }),
      history: [
        {
          id: `bk-${Date.now()}`,
          time: nowStr,
          label: target.label,
          amount: 10,
        },
        ...(stampsState.history || []).slice(0, 14),
      ],
    };
    setStampsState(next);
    saveStampsState(next);
  };

  const handleAddStampType = (e) => {
    e.preventDefault();
    const label = newStampLabel.trim();
    if (!label) return;
    const next = {
      ...stampsState,
      items: [
        ...stampsState.items,
        { id: `custom-${Date.now()}`, label, count: 0, totalBooked: 0 },
      ],
    };
    setNewStampLabel('');
    setStampsState(next);
    saveStampsState(next);
  };

  const handleRemoveStampType = (id) => {
    const next = {
      ...stampsState,
      items: stampsState.items.filter((it) => it.id !== id),
    };
    setStampsState(next);
    saveStampsState(next);
  };

  return (
    <div
      data-testid="wiesn-shop-module"
      style={{
        margin: '8px 0 12px',
        borderRadius: 12,
        border: `1.5px solid ${
          scannerSnap?.popupSuspected
            ? '#ef4444'
            : totalStampsReadyBlocks > 0 || pendingReviewCount > 0
            ? '#eab308'
            : (dark ? '#334155' : '#cbd5e1')
        }`,
        background: dark ? '#161b22' : '#f8fafc',
        overflow: 'hidden',
      }}
    >
      {/* Kompakte Reiter-Leiste oben */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 10px',
          flexWrap: 'wrap',
          background: dark ? '#1e293b' : '#f1f5f9',
        }}
      >
        <button
          type="button"
          data-testid="wiesn-tab-queue"
          onClick={() => setActiveTab((prev) => (prev === 'queue' ? null : 'queue'))}
          style={{
            flex: '1 1 auto',
            padding: '8px 12px',
            borderRadius: 8,
            border: activeTab === 'queue' ? '2px solid #ffcc00' : `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
            background:
              scannerSnap?.popupSuspected
                ? '#ef4444'
                : pendingReviewCount > 0
                ? '#eab308'
                : activeTab === 'queue'
                ? (dark ? '#0f172a' : '#ffffff')
                : 'transparent',
            color:
              scannerSnap?.popupSuspected
                ? '#fff'
                : pendingReviewCount > 0
                ? '#000'
                : (dark ? '#f8fafc' : '#0f172a'),
            fontWeight: 800,
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <span>
            🔌 DHL-Stick {stickEnabled ? (stickStatus?.ok ? '🟢' : '🟡') : '(Aus)'}
            {scannerEyeActive ? ' · 👁️ Auge AN' : ''}
          </span>
          {scannerSnap?.popupSuspected && (
            <span style={{ background: '#000', color: '#ffcc00', borderRadius: 99, padding: '1px 7px', fontSize: 11 }}>
              ⚠️ Popup am Scanner!
            </span>
          )}
          {pendingReviewCount > 0 && (
            <span style={{ background: '#000', color: '#ffcc00', borderRadius: 99, padding: '1px 7px', fontSize: 11 }}>
              {pendingReviewCount} prüfen
            </span>
          )}
          {openSchnellCount > 0 && (
            <span style={{ background: '#2563eb', color: '#fff', borderRadius: 99, padding: '1px 7px', fontSize: 11 }}>
              {openSchnellCount} in Schnelleinl.
            </span>
          )}
          {errorCount > 0 && (
            <span style={{ background: '#dc2626', color: '#fff', borderRadius: 99, padding: '1px 7px', fontSize: 11 }}>
              {errorCount} !
            </span>
          )}
        </button>

        <button
          type="button"
          data-testid="wiesn-tab-stamps"
          onClick={() => setActiveTab((prev) => (prev === 'stamps' ? null : 'stamps'))}
          style={{
            flex: '1 1 auto',
            padding: '8px 12px',
            borderRadius: 8,
            border: activeTab === 'stamps' ? '2px solid #ffcc00' : `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
            background:
              totalStampsReadyBlocks > 0
                ? '#f59e0b'
                : activeTab === 'stamps'
                ? (dark ? '#0f172a' : '#ffffff')
                : 'transparent',
            color: totalStampsReadyBlocks > 0 ? '#000' : (dark ? '#f8fafc' : '#0f172a'),
            fontWeight: 800,
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <span>📮 Briefmarken (10er-Liste)</span>
          {totalStampsReadyBlocks > 0 && (
            <span style={{ background: '#7c2d12', color: '#fff', borderRadius: 99, padding: '1px 8px', fontSize: 11 }}>
              🔔 {totalStampsReadyBlocks}× 10er ausbuchen!
            </span>
          )}
        </button>

        {openSchnellCount > 0 && (
          <button
            type="button"
            data-testid="wiesn-quick-finish-schnell"
            disabled={busyId === 'finish-schnell'}
            onClick={handleFinishSchnell}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: 'none',
              background: '#10b981',
              color: '#000',
              fontWeight: 800,
              fontSize: 12.5,
              cursor: 'pointer',
            }}
          >
            ✅ Schnelleinl. abschließen ({openSchnellCount})
          </button>
        )}
      </div>

      {/* 1. 🔌 Stick-Verbindung, Notfall-Buttons, Scanner-Auge & Warteschlange */}
      {activeTab === 'queue' && (
        <div style={{ padding: 12, borderTop: `1px solid ${dark ? '#334155' : '#e2e8f0'}` }}>
          {actionToast && (
            <div
              style={{
                marginBottom: 10,
                padding: '8px 12px',
                borderRadius: 8,
                background: dark ? '#1e293b' : '#e0f2fe',
                border: '1.5px solid #38bdf8',
                color: dark ? '#f8fafc' : '#0f172a',
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              {actionToast}
            </div>
          )}

          {/* Direkte Stick-Verbindung & Bevollmächtigten-Schalter */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 10px',
              marginBottom: 10,
              borderRadius: 8,
              background: dark ? '#0f172a' : '#ffffff',
              border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
            }}
          >
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                data-testid="wiesn-stick-toggle"
                onClick={() => {
                  const next = !stickEnabled;
                  setDhlStickEnabled(next);
                  setStickEnabledState(next);
                  if (next) refreshStick(stickIpInput);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: 'none',
                  background: stickEnabled ? '#10b981' : '#64748b',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {stickEnabled ? '🟢 Stick AN' : '⚪ Stick AUS (Antippen zum Aktivieren)'}
              </button>

              <button
                type="button"
                data-testid="wiesn-bevollm-toggle"
                onClick={() => {
                  const next = !bevollmEnabled;
                  setBevollmaechtigterEnabled(next);
                  setBevollmEnabledState(next);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1.5px solid ${bevollmEnabled ? '#eab308' : '#64748b'}`,
                  background: bevollmEnabled ? (dark ? 'rgba(234,179,8,0.2)' : '#fef9c3') : 'transparent',
                  color: dark ? '#f8fafc' : '#0f172a',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                👥 Bevollmächtigter: {bevollmEnabled ? 'AN' : 'AUS'}
              </button>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: dark ? '#94a3b8' : '#475569' }}>IP:</span>
                <input
                  type="text"
                  value={stickIpInput}
                  onChange={(e) => setStickIpInput(e.target.value)}
                  onBlur={() => {
                    const clean = setDhlStickIp(stickIpInput);
                    setStickIpInput(clean);
                    refreshStick(clean);
                  }}
                  placeholder="192.168.137.51"
                  style={{
                    width: 126,
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: dark ? '#1e293b' : '#f8fafc',
                    color: dark ? '#f8fafc' : '#0f172a',
                    fontSize: 12,
                    fontFamily: 'monospace',
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const clean = setDhlStickIp(stickIpInput);
                    setStickIpInput(clean);
                    refreshStick(clean);
                  }}
                  style={{
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: 'transparent',
                    color: dark ? '#f8fafc' : '#0f172a',
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  🔄 Verbinden
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: stickStatus?.ok ? '#10b981' : '#f59e0b' }}>
                {stickStatus?.ok
                  ? `🟢 Verbunden (${stickStatus.ip}${stickStatus.lastAction ? ` · ${stickStatus.lastAction}` : ''})`
                  : `⚠️ Stick (${stickIpInput}) direkt nicht erreichbar — Kamera-Brücke nutzbar`}
              </span>
              {typeof onOpenDhlCockpit === 'function' && (
                <button
                  type="button"
                  onClick={onOpenDhlCockpit}
                  style={{
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: '1px solid #eab308',
                    background: 'transparent',
                    color: dark ? '#fde047' : '#a16207',
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  🎯 Kalibrierung
                </button>
              )}
            </div>
          </div>

          {/* 🛠️ Schnell- & Notfall-Steuerung für Scanner 1 */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: '8px 10px',
              marginBottom: 10,
              borderRadius: 8,
              background: dark ? '#0f172a' : '#f1f5f9',
              border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
            }}
          >
            <button
              type="button"
              data-testid="wiesn-safe-home-btn"
              disabled={busyId === 'safe-home'}
              onClick={handleSafeHome}
              style={{
                padding: '6px 10px',
                borderRadius: 7,
                border: 'none',
                background: '#ef4444',
                color: '#fff',
                fontWeight: 800,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
              title="Klickt START (50/80) + JA (95/76) und setzt den Scanner aus jedem Menü sicher auf das Hauptmenü zurück"
            >
              🏠 Not-Reset auf Start
            </button>

            <button
              type="button"
              data-testid="wiesn-remove-last-btn"
              disabled={busyId === 'remove-last'}
              onClick={handleRemoveLastItem}
              style={{
                padding: '6px 10px',
                borderRadius: 7,
                border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                background: dark ? '#1e293b' : '#ffffff',
                color: dark ? '#f8fafc' : '#0f172a',
                fontWeight: 700,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
              title="Wählt die oberste Sendungsnummer in der offenen DHL-Liste (35/22) und klickt auf Entfernen (32/80)"
            >
              🗑️ Letzte Nr. löschen
            </button>

            <button
              type="button"
              disabled={busyId === 'finish-schnell'}
              onClick={handleFinishSchnell}
              style={{
                padding: '6px 10px',
                borderRadius: 7,
                border: 'none',
                background: '#10b981',
                color: '#000',
                fontWeight: 800,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
            >
              ✅ Schnelleinl. abschließen {openSchnellCount > 0 ? `(${openSchnellCount})` : ''}
            </button>

            <button
              type="button"
              onClick={async () => {
                await wakeupDhlStick(stickIpInput);
                showNotice('🔓 Wakeup + PIN 3537 gesendet!');
                refreshStick(stickIpInput);
              }}
              style={{
                padding: '6px 10px',
                borderRadius: 7,
                border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                background: dark ? '#1e293b' : '#ffffff',
                color: dark ? '#f8fafc' : '#0f172a',
                fontWeight: 700,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
            >
              🔓 Wecken + PIN
            </button>

            <button
              type="button"
              disabled={busyId === 'enable-vkbd'}
              onClick={handleEnableVkbd}
              style={{
                padding: '6px 10px',
                borderRadius: 7,
                border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                background: dark ? '#1e293b' : '#ffffff',
                color: dark ? '#f8fafc' : '#0f172a',
                fontWeight: 700,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
              title="Aktiviert auf Scanner 1 einmalig die Android-Bildschirmtastatur trotz eingestecktem USB-Stick"
            >
              ⌨️ Scanner-Tastatur AN
            </button>

            <button
              type="button"
              disabled={busyId === 'ota'}
              onClick={handleOtaUpdate}
              style={{
                padding: '6px 10px',
                borderRadius: 7,
                border: '1px solid #8b5cf6',
                background: dark ? 'rgba(139,92,246,0.18)' : '#f3e8ff',
                color: dark ? '#ddd6fe' : '#6d28d9',
                fontWeight: 800,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
              title="Flasht die neueste Stick-Firmware (v2) drahtlos per WLAN auf den Stick"
            >
              {busyId === 'ota' ? '⏳ Flashe…' : '⚡ Stick-Update (OTA)'}
            </button>

            <button
              type="button"
              data-testid="wiesn-scanner-eye-toggle"
              onClick={handleToggleScannerEye}
              style={{
                padding: '6px 11px',
                borderRadius: 7,
                border: `1.5px solid ${scannerEyeActive ? '#10b981' : '#38bdf8'}`,
                background: scannerEyeActive ? '#10b981' : (dark ? 'rgba(56,189,248,0.15)' : '#e0f2fe'),
                color: scannerEyeActive ? '#000' : (dark ? '#38bdf8' : '#0369a1'),
                fontWeight: 800,
                fontSize: 11.5,
                cursor: 'pointer',
              }}
            >
              {scannerEyeActive ? '👁️ Scanner-Auge & Brücke: AKTIV' : '📷 Dieses Handy als Scanner-Auge starten'}
            </button>
          </div>

          {/* 👁️ Kamera-Brücke (wenn DIESES Handy als Kamera-Station über Scanner 1 liegt) */}
          {scannerEyeActive && (
            <div
              data-testid="wiesn-scanner-eye-monitor"
              style={{
                marginBottom: 12,
                padding: 12,
                borderRadius: 10,
                border: '2px solid #10b981',
                background: '#090d16',
                color: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div>
                  <strong style={{ color: '#10b981', fontSize: 13.5 }}>
                    👁️ Scanner-Auge & WLAN-Brücke aktiv (Richte die Kamera auf Scanner 1)
                  </strong>
                  <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                    Leitet Befehle von allen Arbeits-Handys lokal an den Stick ({stickIpInput}) weiter und sendet Live-Kontrollbilder.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => captureAndBroadcastScannerSnap('Manuell', false)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 7,
                      border: 'none',
                      background: '#38bdf8',
                      color: '#000',
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    📸 Jetzt Bild senden
                  </button>
                  <button
                    type="button"
                    disabled={ocrRunning}
                    onClick={() => captureAndBroadcastScannerSnap('KI-Screenreader', true)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 7,
                      border: 'none',
                      background: '#eab308',
                      color: '#000',
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    {ocrRunning ? '⏳ Lese…' : '🔍 Text lesen (OCR)'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setScannerEyeAutoLoop((v) => !v)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 7,
                      border: '1px solid #475569',
                      background: scannerEyeAutoLoop ? '#10b981' : '#1e293b',
                      color: scannerEyeAutoLoop ? '#000' : '#f8fafc',
                      fontWeight: 700,
                      fontSize: 11.5,
                      cursor: 'pointer',
                    }}
                  >
                    🔄 Auto-Bild (4s): {scannerEyeAutoLoop ? 'AN' : 'AUS'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCropSliders((v) => !v)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 7,
                      border: '1px solid #475569',
                      background: '#1e293b',
                      color: '#f8fafc',
                      fontWeight: 700,
                      fontSize: 11.5,
                      cursor: 'pointer',
                    }}
                  >
                    📐 Rahmen anpassen
                  </button>
                </div>
              </div>

              {eyeCamError && (
                <div style={{ color: '#f87171', fontSize: 12, marginBottom: 8 }}>
                  ❌ Kamera-Fehler: {eyeCamError}
                </div>
              )}

              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 380,
                  margin: '0 auto',
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid #334155',
                  background: '#000',
                }}
              >
                <video
                  ref={eyeVideoRef}
                  playsInline
                  muted
                  autoPlay
                  style={{ width: '100%', display: 'block' }}
                />
                {/* Gelber Rahmen für den Scanner-1-Display-Ausschnitt */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${eyeCrop.x * 100}%`,
                    top: `${eyeCrop.y * 100}%`,
                    width: `${eyeCrop.w * 100}%`,
                    height: `${eyeCrop.h * 100}%`,
                    border: '2.5px dashed #ffcc00',
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                    pointerEvents: 'none',
                  }}
                />
              </div>

              {showCropSliders && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10, fontSize: 11.5 }}>
                  <label>
                    Links ({Math.round(eyeCrop.x * 100)}%):
                    <input type="range" min="0" max="0.45" step="0.02" value={eyeCrop.x} onChange={(e) => updateCropField('x', e.target.value)} style={{ width: '100%' }} />
                  </label>
                  <label>
                    Breite ({Math.round(eyeCrop.w * 100)}%):
                    <input type="range" min="0.25" max="1" step="0.02" value={eyeCrop.w} onChange={(e) => updateCropField('w', e.target.value)} style={{ width: '100%' }} />
                  </label>
                  <label>
                    Oben ({Math.round(eyeCrop.y * 100)}%):
                    <input type="range" min="0" max="0.45" step="0.02" value={eyeCrop.y} onChange={(e) => updateCropField('y', e.target.value)} style={{ width: '100%' }} />
                  </label>
                  <label>
                    Höhe ({Math.round(eyeCrop.h * 100)}%):
                    <input type="range" min="0.3" max="1" step="0.02" value={eyeCrop.h} onChange={(e) => updateCropField('h', e.target.value)} style={{ width: '100%' }} />
                  </label>
                </div>
              )}
            </div>
          )}

          {/* 📺 Live-Kontrollbild vom Scanner-1-Display (empfangen auf dem Arbeits-Handy!) */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 10px',
              marginBottom: 10,
              borderRadius: 8,
              background: dark ? '#0f172a' : '#ffffff',
              border: `1.5px solid ${scannerSnap?.popupSuspected ? '#ef4444' : (scannerSnap?.color || (dark ? '#334155' : '#cbd5e1'))}`,
            }}
          >
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 12.5, color: dark ? '#f8fafc' : '#0f172a' }}>
                  👁️ Scanner-1-Display (Screenreader):
                </strong>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 99,
                    fontSize: 11.5,
                    fontWeight: 800,
                    background: scannerSnap?.color || '#475569',
                    color: '#000',
                  }}
                >
                  {scannerSnap?.label || 'Noch kein Kamera-Bild empfangen'}
                </span>
                <button
                  type="button"
                  onClick={() => requestDhlScreenSnapshot({ delayMs: 250, reason: 'Manuelle Abfrage' })}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: 'transparent',
                    color: dark ? '#38bdf8' : '#0284c7',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  📸 Bild aktualisieren
                </button>
              </div>
              {scannerSnap?.ocrText && (
                <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: dark ? '#1e293b' : '#f1f5f9', fontSize: 11.5, fontFamily: 'monospace', color: dark ? '#fde047' : '#0f172a' }}>
                  <b>Erkannter Text am Scanner:</b> {scannerSnap.ocrText}
                </div>
              )}
              <div style={{ fontSize: 11, color: dark ? '#94a3b8' : '#64748b', marginTop: 4 }}>
                💡 Tipp: Du kannst rechts direkt auf das Scanner-Foto tippen, um genau dort am DHL-Scanner zu klicken!
              </div>
            </div>

            {scannerSnap?.preview && (
              <div
                onClick={handleLiveSnapTap}
                title="Antippen, um genau an dieser Stelle auf dem DHL-Scanner zu klicken"
                style={{
                  position: 'relative',
                  width: 118,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '2px solid #ffcc00',
                  cursor: 'crosshair',
                  flexShrink: 0,
                }}
              >
                <img
                  src={scannerSnap.preview}
                  alt="DHL Scanner Live"
                  style={{ width: '100%', display: 'block' }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'rgba(0,0,0,0.68)',
                    color: '#fff',
                    fontSize: 9.5,
                    textAlign: 'center',
                    padding: '2px 4px',
                  }}
                >
                  {scannerSnap.ts ? new Date(scannerSnap.ts).toLocaleTimeString('de-DE') : ''} · Touch=Klick
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                data-testid="stick-mode-auto"
                onClick={() => handleToggleQueueMode('auto')}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1.5px solid ${queueMode === 'auto' ? '#10b981' : '#64748b'}`,
                  background: queueMode === 'auto' ? (dark ? 'rgba(16,185,129,0.2)' : '#d1fae5') : 'transparent',
                  color: dark ? '#f8fafc' : '#0f172a',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                ⚡ Auto-Modus (sofort an Stick)
              </button>
              <button
                type="button"
                data-testid="stick-mode-review"
                onClick={() => handleToggleQueueMode('review')}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1.5px solid ${queueMode === 'review' ? '#eab308' : '#64748b'}`,
                  background: queueMode === 'review' ? (dark ? 'rgba(234,179,8,0.22)' : '#fef9c3') : 'transparent',
                  color: dark ? '#f8fafc' : '#0f172a',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                🛡️ Prüf-Modus (erst in Liste prüfen)
              </button>
              <button
                type="button"
                data-testid="stick-photo-schnell-toggle"
                onClick={handleTogglePhotoSchnell}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1.5px solid ${photoSchnell ? '#3b82f6' : '#64748b'}`,
                  background: photoSchnell ? (dark ? 'rgba(59,130,246,0.2)' : '#dbeafe') : 'transparent',
                  color: dark ? '#f8fafc' : '#0f172a',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                📸 Foto → sofort Schnelleinlagern: {photoSchnell ? 'AN' : 'AUS'}
              </button>
            </div>

            {tasks.length > 0 && (
              <button
                type="button"
                onClick={() => { clearLocalStickTasks(); setTasks([]); }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                  background: 'transparent',
                  color: dark ? '#94a3b8' : '#64748b',
                  fontSize: 11.5,
                  cursor: 'pointer',
                }}
              >
                🧹 Liste leeren
              </button>
            )}
          </div>

          {tasks.length === 0 ? (
            <div style={{ padding: '10px', textAlign: 'center', fontSize: 12, color: dark ? '#94a3b8' : '#64748b' }}>
              Keine offenen Aufträge in der lokalen Liste.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
              {tasks.map((tItem) => {
                const isPending = tItem.status === 'pending_review';
                const isError = tItem.status === 'error';
                const isSending = tItem.status === 'sending' || busyId === tItem.id;
                return (
                  <div
                    key={tItem.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: `1px solid ${
                        isPending ? '#eab308' : isError ? '#ef4444' : (dark ? '#334155' : '#cbd5e1')
                      }`,
                      background: isPending
                        ? (dark ? 'rgba(234,179,8,0.12)' : '#fefce8')
                        : isError
                        ? (dark ? 'rgba(239,68,68,0.12)' : '#fef2f2')
                        : (dark ? '#0f172a' : '#ffffff'),
                      fontSize: 12.5,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: dark ? '#f8fafc' : '#0f172a' }}>
                        {tItem.type === 'SCHNELLEINLAGERUNG' ? '📥 ' : '📤 '}
                        {tItem.label || tItem.type}
                      </div>
                      <div style={{ fontSize: 11, color: dark ? '#94a3b8' : '#64748b', fontFamily: 'monospace' }}>
                        {(tItem.barcodes || []).join(', ')}
                        {tItem.error ? ` · ⚠️ ${tItem.error}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        disabled={isSending}
                        onClick={() => handleExecuteTask(tItem.id, !isPending)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: isPending ? '#eab308' : '#3b82f6',
                          color: isPending ? '#000' : '#fff',
                          fontWeight: 800,
                          fontSize: 11.5,
                          cursor: 'pointer',
                        }}
                      >
                        {isSending ? '⏳…' : isPending ? '▶️ Jetzt senden' : '🔄 Wiederholen'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLocalStickTask(tItem.id)}
                        style={{
                          padding: '5px 8px',
                          borderRadius: 6,
                          border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                          background: 'transparent',
                          color: '#ef4444',
                          fontSize: 11.5,
                          cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 2. 📮 Briefmarken-Reiter (10er-Strichliste) */}
      {activeTab === 'stamps' && (
        <div style={{ padding: 12, borderTop: `1px solid ${dark ? '#334155' : '#e2e8f0'}` }}>
          {stampsReadyToBook.length > 0 && (
            <div
              data-testid="stamps-alert-banner"
              style={{
                marginBottom: 10,
                padding: '10px 12px',
                borderRadius: 10,
                background: '#fef08a',
                border: '2px solid #ca8a04',
                color: '#713f12',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              🔔 10er-Block erreicht bei: {stampsReadyToBook.map((s) => `${s.label} (${s.count} Stk.)`).join(' · ')} — bitte jetzt im System ausbuchen!
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
            {stampsState.items.map((item) => {
              const cnt = Number(item.count) || 0;
              const isFull = cnt >= 10;
              const pct = Math.min(100, (cnt % 10 === 0 && cnt > 0 ? 10 : cnt % 10) * 10);
              return (
                <div
                  key={item.id}
                  data-testid={`stamp-card-${item.id}`}
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    border: `2px solid ${isFull ? '#eab308' : (dark ? '#334155' : '#cbd5e1')}`,
                    background: isFull
                      ? (dark ? 'rgba(234,179,8,0.18)' : '#fef9c3')
                      : (dark ? '#0f172a' : '#ffffff'),
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 13.5, color: dark ? '#f8fafc' : '#0f172a' }}>{item.label}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          fontWeight: 900,
                          fontSize: 15,
                          padding: '2px 8px',
                          borderRadius: 99,
                          background: isFull ? '#eab308' : (dark ? '#1e293b' : '#e2e8f0'),
                          color: isFull ? '#000' : (dark ? '#f8fafc' : '#0f172a'),
                        }}
                      >
                        {cnt} / 10
                      </span>
                      {String(item.id).startsWith('custom-') && (
                        <button
                          type="button"
                          onClick={() => handleRemoveStampType(item.id)}
                          style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}
                          title="Sorte entfernen"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ height: 8, borderRadius: 99, background: dark ? '#1e293b' : '#e2e8f0', overflow: 'hidden', marginBottom: 8 }}>
                    <div
                      style={{
                        width: `${isFull ? 100 : pct}%`,
                        height: '100%',
                        background: isFull ? '#eab308' : '#10b981',
                        transition: 'width 0.15s ease',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => updateStampCount(item.id, 1)}
                      style={{
                        flex: 1,
                        padding: '7px 8px',
                        borderRadius: 7,
                        border: 'none',
                        background: '#2563eb',
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      +1
                    </button>
                    <button
                      type="button"
                      onClick={() => updateStampCount(item.id, 2)}
                      style={{
                        padding: '7px 9px',
                        borderRadius: 7,
                        border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                        background: dark ? '#1e293b' : '#f1f5f9',
                        color: dark ? '#f8fafc' : '#0f172a',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      +2
                    </button>
                    <button
                      type="button"
                      onClick={() => updateStampCount(item.id, 5)}
                      style={{
                        padding: '7px 9px',
                        borderRadius: 7,
                        border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                        background: dark ? '#1e293b' : '#f1f5f9',
                        color: dark ? '#f8fafc' : '#0f172a',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      +5
                    </button>
                    <button
                      type="button"
                      disabled={cnt <= 0}
                      onClick={() => updateStampCount(item.id, -1)}
                      style={{
                        padding: '7px 9px',
                        borderRadius: 7,
                        border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                        background: 'transparent',
                        color: dark ? '#94a3b8' : '#64748b',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: cnt > 0 ? 'pointer' : 'not-allowed',
                      }}
                    >
                      -1
                    </button>
                  </div>

                  {isFull && (
                    <button
                      type="button"
                      data-testid={`stamp-book10-${item.id}`}
                      onClick={() => bookOutTenStamps(item.id)}
                      style={{
                        width: '100%',
                        marginTop: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: 'none',
                        background: '#eab308',
                        color: '#000',
                        fontWeight: 900,
                        fontSize: 12.5,
                        cursor: 'pointer',
                      }}
                    >
                      ✅ 10 Stück ausgebucht (-10)
                    </button>
                  )}

                  {(Number(item.totalBooked) || 0) > 0 && (
                    <div style={{ fontSize: 10.5, color: dark ? '#94a3b8' : '#64748b', marginTop: 5 }}>
                      Gesamt ausgebucht: {item.totalBooked} Stk.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <form onSubmit={handleAddStampType} style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <input
              type="text"
              value={newStampLabel}
              onChange={(e) => setNewStampLabel(e.target.value)}
              placeholder="Weitere Briefmarken-Sorte (z. B. 3,95 € Einschreiben)…"
              style={{
                flex: 1,
                padding: '7px 10px',
                borderRadius: 8,
                border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                background: dark ? '#0f172a' : '#fff',
                color: dark ? '#f8fafc' : '#0f172a',
                fontSize: 12,
              }}
            />
            <button
              type="submit"
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: 'none',
                background: '#334155',
                color: '#fff',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              + Sorte
            </button>
          </form>

          {Array.isArray(stampsState.history) && stampsState.history.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 11.5, color: dark ? '#94a3b8' : '#64748b' }}>
              <b>Letzte 10er-Ausbuchungen:</b>{' '}
              {stampsState.history.slice(0, 5).map((h) => `${h.time} (${h.amount}× ${h.label})`).join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
